import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Mail, ShieldCheck, ArrowLeft, Eye, EyeOff, Loader2, Fingerprint, Smartphone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from '../components/ImageUpload';
import { useToast } from '../hooks/use-toast';
import {
  passkeySupported,
  isPlatformAuthenticatorAvailable,
  checkAdminPasskey,
  loginAdminWithPasskey,
} from '../lib/passkey';
import { apiErrorMessage } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const { token, login, loading, applyToken } = useAdminAuth();
  const { settings } = useSettings();
  const logoUrl = resolveImageUrl(settings?.logo_image || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pkSupported, setPkSupported] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  // SMS OTP path — toggled by admin in Settings → Notifications.
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsMode, setSmsMode] = useState('hidden'); // hidden | form | code
  const [smsPhone, setSmsPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsMaskedPhone, setSmsMaskedPhone] = useState('');
  const [smsBusy, setSmsBusy] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Post-login destination — supports `?next=/warehouse/floor` so a
  // deep link into the mobile floor page routes the user back to the
  // page they intended after they sign in. Falls back to /admin for
  // backwards-compat with existing admin sign-in flows.
  const nextParam = React.useMemo(() => {
    try {
      const raw = new URLSearchParams(location.search).get('next') || '';
      // Only allow same-origin relative paths — never accept an
      // absolute URL to defeat open-redirect abuse.
      if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
    } catch (_e) { /* noop */ }
    return '';
  }, [location.search]);
  const goDestination = React.useCallback(() => {
    navigate(nextParam || '/admin', { replace: true });
  }, [navigate, nextParam]);

  useEffect(() => {
    (async () => {
      setPkSupported(passkeySupported());
      try {
        const { data } = await axios.get(`${API}/auth/sms-otp-config`);
        setSmsEnabled(!!data?.admin_enabled);
      } catch {
        setSmsEnabled(false);
      }
    })();
  }, []);

  if (token) return <Navigate to={nextParam || '/admin'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    const res = await login(email.trim(), password);
    if (res.ok) {
      toast({ title: 'Welcome back!', description: 'Signed in as admin.' });
      goDestination();
    } else {
      toast({ title: 'Login failed', description: res.error || 'Invalid email or password' });
    }
  };

  const passkeySignIn = async () => {
    const e = email.trim();
    if (!e) {
      toast({ title: 'Enter your admin email first', description: 'We need it to find your passkey.' });
      return;
    }
    setPkBusy(true);
    try {
      const has = await checkAdminPasskey(e);
      if (!has) {
        toast({ title: 'No passkey on this account', description: 'Sign in with password once, then enable a passkey from /admin.' });
        return;
      }
      const { access_token } = await loginAdminWithPasskey(e);
      if (applyToken) applyToken(access_token, e);
      toast({ title: 'Signed in with passkey ✓' });
      goDestination();
    } catch (err) {
      toast({ title: 'Passkey sign-in failed', description: err?.response?.data?.detail || err?.message || 'Try password' });
    } finally {
      setPkBusy(false);
    }
  };

  const requestSmsCode = async () => {
    const e = email.trim();
    const p = smsPhone.trim();
    if (!e || !p) {
      toast({ title: 'Enter your admin email and phone' });
      return;
    }
    setSmsBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-sms-otp`, { email: e, phone: p, role: 'admin' });
      if (data?.masked_phone) setSmsMaskedPhone(data.masked_phone);
      setSmsMode('code');
      toast({ title: 'Code sent', description: `Check your phone (${data?.masked_phone || p}).` });
    } catch (err) {
      toast({ title: 'Could not send code', description: apiErrorMessage(err, 'Failed to send code') });
    } finally {
      setSmsBusy(false);
    }
  };

  const verifySmsCode = async () => {
    const e = email.trim();
    const c = (smsCode || '').trim();
    if (c.length !== 4) {
      toast({ title: 'Enter the 4-digit code' });
      return;
    }
    setSmsBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-sms-otp`, { email: e, code: c });
      if (applyToken) applyToken(data.access_token, data.email);
      toast({ title: 'Signed in via SMS ✓' });
      goDestination();
    } catch (err) {
      toast({ title: 'Invalid code', description: apiErrorMessage(err, 'Invalid code') });
      setSmsCode('');
    } finally {
      setSmsBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen w-full flex items-center justify-center px-5 py-12 relative overflow-hidden bg-[#0B1220]"
      data-testid="admin-login-page"
    >
      {/* Subtle dotted grid background */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      {/* Soft blue glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#0078D4]/20 blur-[120px]" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-6 transition"
          data-testid="admin-login-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to storefront
        </Link>

        <div className="bg-[#111827] border border-white/10 rounded-3xl shadow-2xl p-8 backdrop-blur">
          <div className="flex flex-col items-center gap-3 mb-7 text-center">
            {logoUrl ? (
              <div
                className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center overflow-hidden"
                data-testid="admin-login-logo"
              >
                <img
                  src={logoUrl}
                  alt="Brand logo"
                  className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(0,120,212,0.35)]"
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-[#0078D4] flex items-center justify-center shadow-lg shadow-[#0078D4]/40">
                <ShieldCheck className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-[#60A5FA] uppercase">
                Staff Portal
              </div>
              <h1 className="font-display text-2xl text-white leading-tight mt-1">Admin sign in</h1>
            </div>
          </div>

          <p className="text-sm text-slate-400 -mt-2 mb-6">
            Restricted area — authorized personnel only. Customer accounts cannot sign in here.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Email
              </label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@mrpco.net"
                  autoComplete="username"
                  className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600 focus:border-[#0078D4] focus-visible:ring-[#0078D4]/30"
                  data-testid="admin-login-email"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Password
              </label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-12 pl-10 pr-12 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600 focus:border-[#0078D4] focus-visible:ring-[#0078D4]/30"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  data-testid="admin-login-toggle-pwd"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full h-12 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift mt-2 flex items-center justify-center gap-2"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                'Sign in to dashboard'
              )}
            </Button>
          </form>

          {pkSupported && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                <span className="flex-1 h-px bg-white/10" />
                or
                <span className="flex-1 h-px bg-white/10" />
              </div>
              <button
                type="button"
                onClick={passkeySignIn}
                disabled={pkBusy || !email}
                className="w-full h-12 rounded-full border border-white/15 hover:border-[#0078D4] hover:bg-[#0078D4]/10 text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-login-passkey"
              >
                {pkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4 text-[#60A5FA]" />}
                Sign in with fingerprint / Face ID
              </button>
            </div>
          )}

          {/* SMS OTP login — gated server-side by admin toggle */}
          {smsEnabled && smsMode === 'hidden' && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setSmsMode('form')}
                className="w-full h-11 rounded-full border border-white/10 hover:border-[#0078D4] hover:bg-[#0078D4]/10 text-slate-300 hover:text-white text-sm font-semibold flex items-center justify-center gap-2 transition"
                data-testid="admin-login-sms-toggle"
              >
                <Smartphone className="w-4 h-4 text-[#60A5FA]" />
                Sign in with SMS code
              </button>
            </div>
          )}
          {smsEnabled && smsMode === 'form' && (
            <div className="mt-4 p-4 rounded-2xl border border-[#0078D4]/30 bg-[#0078D4]/5" data-testid="admin-login-sms-form">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.25em] text-[#60A5FA] uppercase flex items-center gap-1.5">
                  <Smartphone className="w-3 h-3" /> SMS sign in
                </span>
                <button
                  type="button"
                  onClick={() => setSmsMode('hidden')}
                  className="text-[10px] text-slate-500 hover:text-white uppercase tracking-wider"
                >
                  Cancel
                </button>
              </div>
              <Input
                type="tel"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                placeholder="+968 9XXX XXXX"
                autoComplete="tel"
                className="h-11 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600 focus:border-[#0078D4]"
                data-testid="admin-login-sms-phone"
              />
              <Button
                type="button"
                onClick={requestSmsCode}
                disabled={smsBusy || !email || !smsPhone}
                className="w-full h-11 mt-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                data-testid="admin-login-sms-send"
              >
                {smsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Text me a 4-digit code <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              <p className="text-[10px] text-slate-500 mt-2">
                Your email and phone must match the admin record on file.
              </p>
            </div>
          )}
          {smsEnabled && smsMode === 'code' && (
            <div className="mt-4 p-4 rounded-2xl border border-[#0078D4]/30 bg-[#0078D4]/5" data-testid="admin-login-sms-code">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.25em] text-[#60A5FA] uppercase">Enter the code</span>
                <button
                  type="button"
                  onClick={() => { setSmsMode('form'); setSmsCode(''); }}
                  className="text-[10px] text-slate-500 hover:text-white uppercase tracking-wider"
                >
                  Back
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-2">Sent to <b className="text-white">{smsMaskedPhone || smsPhone}</b></p>
              <Input
                inputMode="numeric"
                maxLength={4}
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                className="h-12 rounded-xl bg-[#0B1220] border-white/10 text-white text-center text-2xl tracking-[0.4em] font-extrabold focus:border-[#0078D4]"
                data-testid="admin-login-sms-code-input"
              />
              <Button
                type="button"
                onClick={verifySmsCode}
                disabled={smsBusy || smsCode.length !== 4}
                className="w-full h-11 mt-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                data-testid="admin-login-sms-verify"
              >
                {smsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify and sign in'}
              </Button>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between text-xs">
            <span className="text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Encrypted session
            </span>
            <Link
              to="/login"
              className="text-slate-400 hover:text-white transition"
              data-testid="admin-login-customer-link"
            >
              Customer sign in →
            </Link>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          © Mohammed Riaz &amp; Partner LLC · Admin Console
        </p>
      </div>
    </main>
  );
};

export default AdminLogin;
